import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Link as JoyLink,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTheme } from "@mui/joy/styles";
import { Link as RouterLink } from "react-router-dom";

import PageHero from "../components/common/PageHero";
import { getSevaGradientSurface, getSevaGradientConfig } from "../theme/branding";

const SUPPORT_CHANNELS = [
  {
    title: "Call the seva desk",
    description: "Speak with a devotee representative for account help, donation receipts, or seva guidance.",
    actionLabel: "Call now",
    actionHref: "tel:+919918006611",
    icon: PhoneInTalkRoundedIcon,
  },
  {
    title: "Message on WhatsApp",
    description: "Share updates, submit media, or request onboarding via our dedicated WhatsApp helpline.",
    actionLabel: "Open chat",
    actionHref: "https://wa.me/919918006611",
    icon: WhatsAppIcon,
  },
  {
    title: "Email the seva support team",
    description: "Send longer questions, request statements, or coordinate event logistics with our caretakers.",
    actionLabel: "Compose email",
    actionHref: "mailto:support@iskconkanpur.org",
    icon: EmailRoundedIcon,
  },
];

const FAQS = [
  {
    question: "How quickly will I receive donation receipts?",
    answer:
      "Receipts are issued within 24 hours of recording an offering. If you need an urgent acknowledgement, contact the seva desk and we will prioritise it for you.",
  },
  {
    question: "Can I update devotee details after submission?",
    answer:
      "Yes. Visit the devotee profile to edit contact details, seva preferences, or add missing information so their records stay complete.",
  },
  {
    question: "Where do I report media or darshan upload issues?",
    answer:
      "Share the file name or upload timestamp with our WhatsApp helpline. The media sevaks will review and respond with next steps.",
  },
];

const QUICK_LINKS = [
  {
    label: "Coordinate an event",
    href: "/events",
    helper: "Plan festival timelines, assign caretakers, and share schedules with the community.",
    icon: EventAvailableRoundedIcon,
  },
  {
    label: "Review donation ledger",
    href: "/donations",
    helper: "Download statements, confirm offerings, and celebrate monthly impact.",
    icon: ArticleRoundedIcon,
  },
  {
    label: "Browse announcement guides",
    href: "/announcements",
    helper: "Inspire the community with templates, tone guidance, and publish history.",
    icon: ForumRoundedIcon,
  },
];

export default function Support() {
  const theme = useTheme();
  const gradient = getSevaGradientSurface(theme, { withShadow: true });
  const gradientConfig = getSevaGradientConfig(theme.palette.mode);

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <PageHero
        eyebrow="We are here to serve"
        title="Support & seva assistance"
        description="Connect with the ISKCON Kanpur support team for donor care, seva coordination, or technical help."
        actions={
          <>
            <Button
              size="md"
              variant="solid"
              startDecorator={<SupportAgentRoundedIcon />}
              component="a"
              href="tel:+919918006611"
            >
              Speak with a devotee
            </Button>
            <Button size="md" variant="plain" color="neutral" component="a" href="mailto:support@iskconkanpur.org">
              Send an email
            </Button>
          </>
        }
      />

      <Grid container spacing={3}>
        {SUPPORT_CHANNELS.map((channel) => {
          const Icon = channel.icon;
          return (
            <Grid key={channel.title} xs={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "xl",
                  height: "100%",
                  p: { xs: 2.5, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.8,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Sheet
                    variant="soft"
                    color="primary"
                    sx={{
                      borderRadius: "md",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon fontSize="small" />
                  </Sheet>
                  <Typography level="title-md" sx={{ fontWeight: 700 }}>
                    {channel.title}
                  </Typography>
                </Stack>
                <Typography level="body-sm" sx={{ color: "text.secondary", flexGrow: 1 }}>
                  {channel.description}
                </Typography>
                <Button
                  component="a"
                  href={channel.actionHref}
                  variant="soft"
                  color="primary"
                  endDecorator={<ArrowForwardRoundedIcon />}
                >
                  {channel.actionLabel}
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid xs={12} md={7}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "100%",
            }}
          >
            <Stack spacing={0.75}>
              <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                Frequently asked questions
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Quick guidance for the most common stewardship needs. Reach out if you require personalised assistance.
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {FAQS.map((faq) => (
                <Box key={faq.question}>
                  <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {faq.answer}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Chip
              variant="soft"
              color="neutral"
              startDecorator={<AccessTimeRoundedIcon />}
              sx={{ alignSelf: "flex-start" }}
            >
              Response time: within 12–24 hours
            </Chip>
          </Card>
        </Grid>
        <Grid xs={12} md={5}>
          <Sheet
            sx={{
              ...gradient,
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              color: gradient.color,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "100%",
            }}
          >
            <Stack spacing={0.75}>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                Visit the Kanpur temple office
              </Typography>
              <Typography level="body-sm" sx={{ color: gradientConfig.muted }}>
                Join us in person for seva coordination meetings, media submissions, or donation assistance.
              </Typography>
            </Stack>

            <List
              size="sm"
              sx={{
                "--ListItem-radius": (theme) => theme.vars.radius.md,
                "--List-gap": "12px",
                bgcolor: "rgba(255,255,255,0.12)",
                p: 1.5,
                borderRadius: "xl",
                border: `1px solid ${gradientConfig.panelBorder}`,
              }}
            >
              <ListItem>
                <ListItemDecorator>
                  <LocationOnRoundedIcon fontSize="small" />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">
                    Bithoor Road, Main ISKCON Temple Campus, Kanpur, Uttar Pradesh
                  </Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator>
                  <AccessTimeRoundedIcon fontSize="small" />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">Support hours: 9:00 AM – 7:00 PM IST</Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator>
                  <SupportAgentRoundedIcon fontSize="small" />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">Seva desk extension: 204</Typography>
                </ListItemContent>
              </ListItem>
            </List>

            <Box>
              <Typography level="body-sm" sx={{ color: gradientConfig.muted }}>
                Prefer to self-serve? Explore our guides and announcements for tips on orchestrating devotional events with joy.
              </Typography>
              <JoyLink
                component={RouterLink}
                to="/articles"
                underline="always"
                sx={{ color: gradientConfig.foreground, fontWeight: 600, mt: 1, display: "inline-flex", gap: 0.5 }}
              >
                View stewardship handbook
              </JoyLink>
            </Box>
          </Sheet>
        </Grid>
      </Grid>

      <Card
        variant="outlined"
        sx={{
          borderRadius: "xl",
          p: { xs: 2.5, md: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Quick links for caretakers
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Jump straight into the workspaces that streamline seva coordination and donor care.
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={1.5}>
          {QUICK_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <Grid key={link.label} xs={12} sm={4}>
                <Sheet
                  variant="soft"
                  color="neutral"
                  sx={{
                    borderRadius: "xl",
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Sheet
                      variant="solid"
                      color="primary"
                      sx={{
                        borderRadius: "md",
                        p: 0.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon fontSize="small" />
                    </Sheet>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {link.label}
                    </Typography>
                  </Stack>
                  <Typography level="body-sm" sx={{ color: "text.secondary", flexGrow: 1 }}>
                    {link.helper}
                  </Typography>
                  <Button component={RouterLink} to={link.href} variant="plain" color="neutral" sx={{ px: 0 }}>
                    Open workspace
                  </Button>
                </Sheet>
              </Grid>
            );
          })}
        </Grid>
      </Card>
    </Stack>
  );
}
